import { ChevronLeftIcon, XIcon } from '@heroicons/react/outline';
import { Button, Drawer } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { Note } from '@/components/PdfReader/GeneratePdf/NotePdfDocument';
import NoteContent from '@/components/PdfReader/PdfNotes/NoteContent';
import NotesDrawer from '@/components/PdfReader/PdfNotes/NotesDrawer';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { generateAndDownloadPdf } from '@/components/shared/generateAndDownloadPdf';

import { drawerPdfAtom } from '@/atoms/pdfStates/drawerAtom';
import { isFullScreenAtom } from '@/atoms/pdfStates/isFullScreen';
import { notesAtom } from '@/atoms/pdfStates/notesAtom';
import { getNotesByBookId, IGroupedNotes } from '@/services/Notes';

interface Props {
  landingPageUrl: string;
  bookId?: string;
  bookTitle?: string;
  bookAuthor?: string;
}

const NotesApp: React.FC<Props> = ({
  landingPageUrl,
  bookId,
  bookTitle,
  bookAuthor,
}) => {
  const [selectedNote, setSelectedNote] = useState<any>(null);
  const isFullScreen = useRecoilValue(isFullScreenAtom);
  const [drawerState, setDrawerState] = useRecoilState(drawerPdfAtom);
  const [notes, setNotes] = useRecoilState<Record<string, Note[]>>(notesAtom);
  const screens = useBreakpoint();
  const handleNoteSelect = (note: any) => {
    setSelectedNote(note);
    setDrawerState({
      contentDrawer: true,
      notesDrawer: false,
    });
  };

  const isEmptyNotes = Object.values(notes).every((arr) => arr.length === 0);

  const handleBackToNotes = () => {
    setDrawerState({
      contentDrawer: false,
      notesDrawer: true,
    });
  };

  const formatNotesData = (
    chapters: IGroupedNotes[],
  ): Record<string, Note[]> => {
    const notesData: Record<string, Note[]> = {};

    chapters.forEach((chapter, index) => {
      const chapterTitle = chapter.chapterName || `Chapter ${index + 1}`;
      notesData[chapterTitle] = chapter.notes.map((note) => ({
        id: note._id,
        title: chapterTitle,
        excerpt: note.excerpt,
        content: note.content,
        chapter: `Chapter ${note.chapterNumber}`,
        date: moment(note.createdAt || '').format('MMMM D, YYYY'),
        pageNumber: note.pageNumber,
      }));
    });

    return notesData;
  };

  const fetchNotes = async () => {
    try {
      if (!bookId) return;
      const allNotes = await getNotesByBookId(bookId);
      const formattedNotes = formatNotesData(allNotes as IGroupedNotes[]);
      setNotes(formattedNotes);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  };

  useEffect(() => {
    if (bookId && drawerState.notesDrawer) {
      fetchNotes();
    }
  }, [bookId, drawerState.notesDrawer]);

  const handleDownload = () =>
    generateAndDownloadPdf({
      notes: Object.values(notes).flat(),
      title: bookTitle || 'Zebralearn Book Notes',
      author: bookAuthor || 'Zebralearn',
      landingPageUrl: landingPageUrl,
    });

  return (
    <div className='min-h-screen bg-[#1e1e1e] text-white'>
      <Drawer
        title={
          <div className='relative w-full flex items-center justify-start h-10'>
            <div className='text-lg font-semibold'>My Notes</div>

            <div className='absolute right-2'>
              <div className='flex gap-x-3'>
                <ShouldRender check={screens.md}>
                  <ButtonDefault
                    size={1}
                    variant={ButtonVariants.WHITE}
                    onClick={handleDownload}
                    className='w-min  whitespace-nowrap'
                  >
                    <div className=' px-4'>Download as PDF</div>
                  </ButtonDefault>
                </ShouldRender>

                <Button
                  onClick={() =>
                    setDrawerState((prev) => ({
                      ...prev,
                      notesDrawer: false,
                    }))
                  }
                  className='border-none bg-transparent shadow-none'
                  icon={<XIcon className='w-4 h-4 text-white' />}
                />
              </div>
            </div>
          </div>
        }
        placement='right'
        onClose={() =>
          setDrawerState((prev) => ({
            ...prev,
            notesDrawer: false,
          }))
        }
        {...(isFullScreen && { getContainer: false })}
        open={drawerState.notesDrawer}
        closeIcon={null}
        width={650}
        classNames={{
          body: 'hide-scrollbar',
          content: 'hide-scrollbar',
          wrapper: 'hide-scrollbar',
        }}
        styles={{
          header: {
            background: '#121212',
            color: 'white',
            borderBottom: '1px solid #333',
            padding: '12px 16px',
          },
          body: {
            background: '#121212',
            padding: 0,
          },
          mask: { background: 'rgba(0, 0, 0, 0.2)' },
        }}
        destroyOnHidden
      >
        {isEmptyNotes ? (
          <div className='h-full w-full flex flex-col items-center justify-center text-white px-4 text-center'>
            <div className='text-base font-semibold mb-2'>No Notes Taken</div>
            <p className='text-sm text-grayZl-500'>
              Engage in active reading by taking notes while you read.
            </p>
          </div>
        ) : (
          <NotesDrawer
            notes={notes}
            onNoteSelect={handleNoteSelect}
            landingPageUrl={landingPageUrl}
            bookAuthor={bookAuthor}
            bookTitle={bookTitle}
            isFullScreen={isFullScreen}
          />
        )}
      </Drawer>

      <Drawer
        title={
          <div className='relative w-full flex justify-center items-center'>
            <button
              onClick={handleBackToNotes}
              className='absolute left-0 flex justify-center items-center w-6 h-6 rounded-full bg-[#3c3b3e] cursor-pointer'
            >
              <ChevronLeftIcon className='w-4 h-4' />
            </button>
            <div className='text-lg font-semibold leading-6'>
              {selectedNote?.chapter}
            </div>
          </div>
        }
        {...(isFullScreen && { getContainer: false })}
        placement='right'
        extra={
          <Button
            onClick={() =>
              setDrawerState((prev) => ({
                ...prev,
                contentDrawer: false,
              }))
            }
            className='border-none'
          >
            <XIcon className='w-4' />
          </Button>
        }
        onClose={() =>
          setDrawerState((prev) => ({
            ...prev,
            contentDrawer: false,
          }))
        }
        open={drawerState.contentDrawer}
        closeIcon={null}
        width={650}
        styles={{
          header: {
            background: '#121212',
            color: 'white',
            borderBottom: '1px solid #333',
          },
          body: {
            background: '#121212',
            padding: 0,
          },
          mask: { background: 'rgba(0, 0, 0, 0.2)' },
        }}
      >
        {selectedNote && (
          <NoteContent
            note={selectedNote}
            onClose={() =>
              setDrawerState((prev) => ({
                ...prev,
                contentDrawer: false,
              }))
            }
            navigateBack={handleBackToNotes}
          />
        )}
      </Drawer>
    </div>
  );
};

export default NotesApp;
